/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useEffect, useState } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Icon from 'src/components/Icon'
import {
  captionXSmall,
  paragraphSmall,
  paragraphXSmall,
  weightRegular,
  weightSemiBold,
} from 'src/components/css'
import { shareLinkParts } from '../utils/shareLinkParts'

interface ShareLinkFieldProps {
  /** The link itself. SERVER-BUILT — never composed at the call site. */
  link: string
  /** Caption above the field, e.g. "Team link". */
  label: string
  /** Test hook on the field wrapper. */
  testId?: string
}

/** How long "Copied" stands before the button returns to its label. */
const COPIED_MS = 2000

/**
 * SN-4709 Team Privacy (Inon, Sep 2) — a share link, shown as a field.
 *
 * This replaces `CopyComponent` AT THIS CALL SITE ONLY, and deliberately does not
 * change it: that component is a chip for short ids ("ID · a3f9x2") and is
 * correct in the ~25 places that use it that way. It was simply never a URL
 * field, and a 97-character link exposed every way in which it is not — it
 * inherits `captionXSmall`'s 1.2px tracking at 12px, wraps mid-token through
 * `overflow-wrap: anywhere`, and sets its own label LARGER than the value it
 * labels.
 *
 * What this is instead: the house form-field treatment (40px, 8px radius,
 * `SECONDARY_100` border) so it reads as something you operate, with the copy
 * action as a real labelled button rather than a bare 16px icon. A share link is
 * scanned for plausibility, not read, so the value stays on ONE line.
 *
 * The two spans are the middle-truncation mechanism, and they are load-bearing:
 * the head shrinks and takes the CSS ellipsis while the tail cannot shrink at
 * all, so the origin and the team token both survive at whatever width the field
 * has. See `shareLinkParts` for why both ends matter. Doing this with `flex`
 * rather than measurement means it re-truncates on resize for free.
 */
const ShareLinkField = ({ link, label, testId }: ShareLinkFieldProps) => {
  'use memo'

  const [hasCopied, setHasCopied] = useState(false)
  const { head, tail } = shareLinkParts(link)

  /*
   * Cleared on unmount — `CopyComponent` leaves this timer running and calls
   * setState on a component that may be gone. A registration review can be
   * navigated away from inside the two seconds.
   */
  useEffect(() => {
    if (!hasCopied) {
      return undefined
    }
    const timer = setTimeout(() => setHasCopied(false), COPIED_MS)

    return () => clearTimeout(timer)
  }, [hasCopied])

  return (
    <div css={wrapperStyle} data-testid={testId}>
      <span css={labelStyle}>{label}</span>
      <div css={fieldStyle}>
        {/*
          `title` carries the WHOLE link: the visible value is deliberately
          incomplete, so hovering has to be able to answer "incomplete how?".
        */}
        <span css={valueStyle} title={link}>
          <span css={headStyle}>{head}</span>
          {tail ? <span css={tailStyle}>{tail}</span> : null}
        </span>
        <button
          type='button'
          css={buttonStyle}
          aria-label={`Copy ${label}`}
          onClick={() => {
            // Optional-chained: a non-secure origin has no clipboard API at all,
            // and an unguarded call there is a TypeError rather than a no-op.
            navigator.clipboard?.writeText(link)
            setHasCopied(true)
          }}
        >
          <Icon
            faType='far'
            name={hasCopied ? 'clipboard-check' : 'clipboard'}
            fontSize={16}
            color={PRIMARY_BUTTON_CONTENT.icon}
          />
          {capitalizeWords(hasCopied ? 'Copied' : 'Copy Link')}
        </button>
      </div>
    </div>
  )
}

export default ShareLinkField

// ---- styled ----

const wrapperStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  min-width: 0;
`

/** A caption in the exact sense the preset was written for. */
const labelStyle = css`
  ${captionXSmall}
  color: ${colors.NEUTRAL_70};
`

const fieldStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  min-width: 0;
  /* The app ships no global border-box reset, so without this the 1px border
     puts the control at 42px and off the 40px height every other field on the
     screen holds to. */
  box-sizing: border-box;
  height: 40px;
  padding: 0 4px 0 12px;
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_100};
  background: ${colors.SECONDARY_400};
`

/**
 * `min-width: 0` on every link in the chain, or the head refuses to shrink below
 * its content width and pushes the button out of the field instead of
 * ellipsising — the same trap `CopyComponent` papered over with
 * `overflow-wrap: anywhere`.
 */
const valueStyle = css`
  ${paragraphSmall}
  ${weightRegular}
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  color: ${colors.NEUTRAL_30};
  white-space: nowrap;
`

const headStyle = css`
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`

/** Never shrinks: this end is the team token, and losing it defeats the field. */
const tailStyle = css`
  flex: 0 0 auto;
`

/**
 * The card's only primary action, so it carries the tenant fill — and therefore
 * takes its text AND icon colour from `PRIMARY_BUTTON_CONTENT`, which is dark on
 * SportNinja's amber and white on Canlan's blue.
 */
const buttonStyle = css`
  ${paragraphXSmall}
  ${weightSemiBold}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-shrink: 0;
  height: 32px;
  padding: 0 12px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
  background: ${colors.PRIMARY};
  color: ${PRIMARY_BUTTON_CONTENT.text};
  transition: opacity 0.15s ease-in-out;

  &:hover {
    opacity: 0.85;
  }
`
